/** B2 — Cartera de cobros: lo pendiente de cada cliente por antigüedad del vencimiento. */
import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { Loader2 } from 'lucide-react'
import { api } from '../../lib/api'
import { formatCents } from '../../lib/money'
import { Note, td, tdR, th, thR } from './common'
import type { ReceivablesResponse } from '@toga/shared'

export default function ReceivablesTab({
  issuerId,
  clientId,
}: {
  issuerId: string
  clientId: string
}) {
  const [data, setData] = useState<ReceivablesResponse | null>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let alive = true
    setData(null)
    setError(null)
    const p = new URLSearchParams()
    if (issuerId) p.set('issuerId', issuerId)
    if (clientId) p.set('clientId', clientId)
    api
      .get<ReceivablesResponse>(`/accounting/receivables?${p}`)
      .then((r) => alive && setData(r))
      .catch((err) => alive && setError(err instanceof Error ? err.message : 'Error al cargar'))
    return () => {
      alive = false
    }
  }, [issuerId, clientId])

  if (error) return <div className="p-5 text-xs text-rose-300">{error}</div>
  if (!data) {
    return (
      <div className="px-5 py-8 t-muted text-sm flex items-center gap-2">
        <Loader2 size={14} className="animate-spin" /> Cargando…
      </div>
    )
  }
  if (data.rows.length === 0) {
    return (
      <div className="px-5 py-10 text-center t-muted text-sm">No hay nada pendiente de cobro.</div>
    )
  }
  const t = data.totals
  return (
    <div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-base">
              <th className={th}>Cliente</th>
              <th className={thR}>Facturas</th>
              <th className={thR}>No vencido</th>
              <th className={thR}>1–30 días</th>
              <th className={thR}>31–60 días</th>
              <th className={thR}>61–90 días</th>
              <th className={thR}>Más de 90</th>
              <th className={thR}>Total pendiente</th>
            </tr>
          </thead>
          <tbody>
            {data.rows.map((r) => (
              <tr key={r.clientId} className="border-b border-base/50 last:border-0">
                <td className={td}>
                  <Link to={`/clients/${r.clientId}?tab=facturacion`} className="hover:underline">
                    {r.clientName}
                  </Link>
                </td>
                <td className={tdR}>{r.invoiceCount}</td>
                <td className={tdR}>{formatCents(r.notDueCents)}</td>
                <td className={tdR}>{r.late1to30Cents ? formatCents(r.late1to30Cents) : '—'}</td>
                <td className={`${tdR} ${r.late31to60Cents ? 'text-amber-300' : ''}`}>
                  {r.late31to60Cents ? formatCents(r.late31to60Cents) : '—'}
                </td>
                <td className={`${tdR} ${r.late61to90Cents ? 'text-amber-300' : ''}`}>
                  {r.late61to90Cents ? formatCents(r.late61to90Cents) : '—'}
                </td>
                <td className={`${tdR} ${r.lateOver90Cents ? 'text-rose-300' : ''}`}>
                  {r.lateOver90Cents ? formatCents(r.lateOver90Cents) : '—'}
                </td>
                <td className={`${tdR} font-semibold`}>{formatCents(r.totalCents)}</td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr className="border-t border-base font-semibold">
              <td className={td}>Total</td>
              <td className={tdR}>{t.invoiceCount}</td>
              <td className={tdR}>{formatCents(t.notDueCents)}</td>
              <td className={tdR}>{formatCents(t.late1to30Cents)}</td>
              <td className={tdR}>{formatCents(t.late31to60Cents)}</td>
              <td className={tdR}>{formatCents(t.late61to90Cents)}</td>
              <td className={tdR}>{formatCents(t.lateOver90Cents)}</td>
              <td className={tdR}>{formatCents(t.totalCents)}</td>
            </tr>
          </tfoot>
        </table>
      </div>
      <Note>
        A fecha de {new Date(data.asOf).toLocaleDateString('es-ES')}. Importes con IVA, menos la
        retención y lo ya cobrado; la antigüedad cuenta desde el vencimiento de cada factura (o su
        fecha de emisión si no tiene). Las facturas sustituidas por otra no se cobran: se cobra la
        sustitutiva.
      </Note>
    </div>
  )
}
